import { PlusOutlined } from '@ant-design/icons';
import { App, Upload, type UploadFile, type UploadProps } from 'antd';
import React, { useState } from 'react';
import fileService from '@/services/file';

type UploadValue = number | number[] | undefined;

type UploadResponse = API.FileItem;

type ImageUploadFile = UploadFile<UploadResponse> & {
  fileId?: number;
};

export type ImageUploadInitialFile = {
  fileId: number;
  name?: string;
  url: string;
};

interface ImageUploadProps {
  value?: UploadValue;
  onChange?: (value: UploadValue) => void;
  source: number;
  maxCount?: number;
  maxSizeInMB?: number;
  accept?: string;
  allowedTypes?: string[];
  disabled?: boolean;
  initialFiles?: ImageUploadInitialFile[];
  placeholder?: string;
}

const getUploadedFileId = (file: ImageUploadFile) => file.response?.id ?? file.fileId;
const getPreviewUrl = (file?: UploadResponse) => file?.storageUrl || file?.path;

const buildFileList = (files: ImageUploadInitialFile[]): ImageUploadFile[] =>
  files.map((file) => ({
    uid: `uploaded-${file.fileId}`,
    name: file.name || `图片-${file.fileId}`,
    status: 'done',
    url: file.url,
    fileId: file.fileId,
  }));

const ImageUpload: React.FC<ImageUploadProps> = ({
  onChange,
  source,
  maxCount = 1,
  maxSizeInMB,
  accept = 'image/*',
  allowedTypes,
  disabled = false,
  initialFiles = [],
  placeholder = '上传图片',
}) => {
  const { message } = App.useApp();
  const [fileList, setFileList] = useState<ImageUploadFile[]>(() =>
    buildFileList(initialFiles),
  );

  const emitChange = (nextFileList: ImageUploadFile[]) => {
    const uploadedIds = nextFileList
      .filter((file) => file.status === 'done')
      .map((file) => getUploadedFileId(file))
      .filter((fileId): fileId is number => typeof fileId === 'number');

    if (maxCount === 1) {
      onChange?.(uploadedIds[0]);
      return;
    }

    onChange?.(uploadedIds);
  };

  const normalizeFileList = (nextFileList: ImageUploadFile[]): ImageUploadFile[] =>
    nextFileList.map((file): ImageUploadFile => {
      const previewUrl = getPreviewUrl(file.response);
      if (!previewUrl) {
        return file;
      }

      return {
        ...file,
        status: file.status === 'error' ? 'error' : 'done',
        url: previewUrl,
        thumbUrl: previewUrl,
        fileId: getUploadedFileId(file),
      };
    });

  const updateUploadedFile = (uid: string, uploadedFile: UploadResponse) => {
    setFileList((current) => {
      const nextFileList = normalizeFileList(
        current.map((file) =>
          file.uid === uid
            ? ({
                ...file,
                status: 'done',
                response: uploadedFile,
                fileId: uploadedFile.id,
              } as ImageUploadFile)
            : file,
        ),
      );
      emitChange(nextFileList);
      return nextFileList;
    });
  };

  const validateFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      message.error('请上传图片文件');
      return Upload.LIST_IGNORE;
    }

    if (allowedTypes?.length) {
      const fileName = file.name.toLowerCase();
      const matched = allowedTypes.some((type) => {
        const normalizedType = type.toLowerCase();
        return normalizedType.startsWith('.')
          ? fileName.endsWith(normalizedType)
          : file.type.toLowerCase() === normalizedType;
      });

      if (!matched) {
        message.error(`仅支持 ${allowedTypes.join(' / ')} 格式`);
        return Upload.LIST_IGNORE;
      }
    }

    if (maxSizeInMB && file.size > maxSizeInMB * 1024 * 1024) {
      message.error(`图片大小不能超过 ${maxSizeInMB}MB`);
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const handleChange: UploadProps<UploadResponse>['onChange'] = ({ fileList: nextFileList }) => {
    const normalizedFileList = normalizeFileList(nextFileList as ImageUploadFile[]);
    setFileList(normalizedFileList);
  };

  return (
    <Upload<UploadResponse>
      accept={accept}
      beforeUpload={validateFile}
      customRequest={async ({ file, onError, onSuccess }) => {
        try {
          const uploadedFile = await fileService.uploadFile(file as File, source);
          updateUploadedFile((file as ImageUploadFile).uid, uploadedFile);
          onSuccess?.(uploadedFile);
        } catch (error) {
          onError?.(error as Error);
          message.error(error instanceof Error ? error.message : '文件上传失败');
        }
      }}
      disabled={disabled}
      fileList={fileList}
      listType="picture-card"
      maxCount={maxCount}
      multiple={maxCount > 1}
      onChange={handleChange}
      onPreview={(file) => {
        const previewUrl = file.url || file.thumbUrl;
        if (previewUrl) {
          window.open(previewUrl, '_blank', 'noopener,noreferrer');
        }
      }}
      onRemove={(file) => {
        const nextFileList = fileList.filter((item) => item.uid !== file.uid);
        setFileList(nextFileList);
        emitChange(nextFileList);
        return true;
      }}
    >
      {fileList.length >= maxCount ? null : (
        <div>
          <PlusOutlined />
          <div style={{ marginTop: 8 }}>{placeholder}</div>
        </div>
      )}
    </Upload>
  );
};

export default ImageUpload;
